import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Radio,
  Checkbox,
  Upload,
  Button,
  Row,
  Col,
  message,
  Card,
  Divider,
} from "antd";
import { UploadOutlined, InboxOutlined } from "@ant-design/icons";
import moment from "moment";
import Service from "../../service";
import { useDispatch } from "react-redux";
import { showAuthLoader, hideAuthLoader } from "../../appRedux/actions/Auth";
import {
  SAME_AS_PRESENT_ADDRESS_LABEL,
  PRESENT_ADDRESS_LAST_FIELD,
  PERMANENT_TO_PRESENT_MAP,
} from "../../constants/onboardingPredefinedFields";

const { Option } = Select;
const { TextArea } = Input;
const { Dragger } = Upload;

const DynamicFormRenderer = ({
  formConfig,
  onSubmit,
  initialValues = {},
  disabled = false,
  showSubmitButton = true,
  submitButtonText = "Submit",
  showResetButton = true,
  resetButtonText = "Reset",
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [dropdownData, setDropdownData] = useState({});
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [fileList, setFileList] = useState({});

  const dropdownCache = React.useRef({});

  const defaultFieldNames = [
    "initialName",
    "first_name",
    "last_name",
    "email",
    "experience",
    "designation_id",
    "department_id",
    "sub_department_id",
  ];

  useEffect(() => {
    let isMounted = true;

    if (formConfig && formConfig.fields && !dataLoaded) {
      loadDropdownData(isMounted);
    }

    return () => {
      isMounted = false;
    };
  }, [formConfig, dataLoaded]);

  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues]);

  const loadDropdownData = async (isMounted = true) => {
    try {
      if (!isMounted) return;
      setLoading(true);

      const dropdownFields = formConfig.fields.filter(
        (field) =>
          field.fieldType === "dropdown" &&
          [
            "designation_id",
            "department_id",
            "sub_department_id",
            "business_unit",
            "business_head",
            "function_role_id",
          ].includes(field.name)
      );

      const apiEndpoints = new Map();
      dropdownFields.forEach((field) => {
        let apiUrl = "";
        switch (field.name) {
          case "designation_id":
            apiUrl = Service.empdesignationDropdownList;
            break;
          case "department_id":
            apiUrl = Service.empmasterdepartment;
            break;
          case "sub_department_id":
            apiUrl = Service.subDepartment;
            break;
          case "business_unit":
            apiUrl = Service.businessUnit;
            break;
          case "business_head":
          case "function_role_id":
            apiUrl = Service.empDropdownList;
            break;
          default:
            return;
        }

        if (apiUrl && !apiEndpoints.has(apiUrl)) {
          apiEndpoints.set(apiUrl, []);
        }
        if (apiUrl) {
          apiEndpoints.get(apiUrl).push(field.name);
        }
      });

      const promises = Array.from(apiEndpoints.entries()).map(
        async ([apiUrl, fieldNames]) => {
          if (!isMounted) return;

          const cacheKey = apiUrl;
          if (dropdownCache.current[cacheKey]) {
            if (isMounted) {
              fieldNames.forEach((fieldName) => {
                setDropdownData((prev) => ({
                  ...prev,
                  [fieldName]: dropdownCache.current[cacheKey],
                }));
              });
            }
            return;
          }

          try {
            const method =
              apiUrl === Service.subDepartment
                ? Service.getMethod
                : Service.postMethod;
            const response = await Service.makeAPICall({
              methodName: method,
              api_url: apiUrl,
            });

            if (response.data && response.data.data && isMounted) {
              dropdownCache.current[cacheKey] = response.data.data;

              fieldNames.forEach((fieldName) => {
                setDropdownData((prev) => ({
                  ...prev,
                  [fieldName]: response.data.data,
                }));
              });
            }
          } catch (error) {
            console.log(`Error loading ${apiUrl}:`, error);
          }
        }
      );

      await Promise.all(promises);
      if (isMounted) {
        setDataLoaded(true);
      }
    } catch (error) {
      console.log("Error loading dropdown data:", error);
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  const getValidationRules = (field) => {
    const rules = [];

    if (field.required) {
      rules.push({
        required: true,
        message: `Please ${
          field.fieldType === "file" ? "upload" : "enter"
        } ${field.label.toLowerCase()}!`,
      });
    }

    if (field.validation) {
      if (field.validation.minLength) {
        rules.push({
          min: field.validation.minLength,
          message: `${field.label} should be at least ${field.validation.minLength} characters`,
        });
      }

      if (field.validation.maxLength) {
        rules.push({
          max: field.validation.maxLength,
          message: `${field.label} should be less than ${field.validation.maxLength} characters`,
        });
      }

      if (field.validation.pattern) {
        rules.push({
          pattern: new RegExp(field.validation.pattern),
          message:
            field.validation.customMessage ||
            `Please enter a valid ${field.label.toLowerCase()}`,
        });
      }

      if (field.validation.min !== undefined) {
        rules.push({
          type: "number",
          min: field.validation.min,
          message: `${field.label} should be at least ${field.validation.min}`,
        });
      }

      if (field.validation.max !== undefined) {
        rules.push({
          type: "number",
          max: field.validation.max,
          message: `${field.label} should be at most ${field.validation.max}`,
        });
      }
    }

    if (field.fieldType === "email") {
      rules.push({
        type: "email",
        message: "Please enter a valid email address!",
      });
    }

    if (field.fieldType === "phone") {
      rules.push({
        pattern: /^(0\d{6,9}|\+?[1-9]\d{0,2}([\s-]?\d{1,4}){1,5})$/,
        message: "Please enter a valid phone number!",
      });
    }

    return rules;
  };

  const renderField = (field) => {
    const commonProps = {
      placeholder: field.placeholder,
      disabled: disabled || field.disabled,
      style: field.styling?.style || { width: field.styling?.width || "100%" },
      className: field.styling?.className,
    };

    switch (field.fieldType) {
      case "text":
        return <Input {...commonProps} />;

      case "email":
        return <Input {...commonProps} type="email" />;

      case "number":
        return (
          <InputNumber
            {...commonProps}
            style={{ width: "100%" }}
            min={field.validation?.min}
            max={field.validation?.max}
          />
        );

      case "password":
        return <Input.Password {...commonProps} />;

      case "textarea":
        return <TextArea {...commonProps} rows={3} />;

      case "url":
        return <Input {...commonProps} type="url" />;

      case "phone":
        return <Input {...commonProps} />;

      case "select":
      case "dropdown":
        if (field.name === "designation_id") {
          return (
            <Select
              {...commonProps}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {dropdownData[field.name]?.map((item) => (
                <Option key={item._id} value={item._id}>
                  {item.designation_name}
                </Option>
              ))}
            </Select>
          );
        }

        if (field.name === "department_id") {
          return (
            <Select
              {...commonProps}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {dropdownData[field.name]?.map((item) => (
                <Option key={item._id} value={item._id}>
                  {item.department_name}
                </Option>
              ))}
            </Select>
          );
        }

        if (field.name === "sub_department_id") {
          return (
            <Select
              {...commonProps}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {dropdownData[field.name]?.map((item) => (
                <Option key={item._id} value={item._id}>
                  {item.sub_department_name}
                </Option>
              ))}
            </Select>
          );
        }

        if (field.name === "business_unit") {
          return (
            <Select
              {...commonProps}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {dropdownData[field.name]?.map((item) => (
                <Option key={item._id} value={item._id}>
                  {item.businessUnitName}
                </Option>
              ))}
            </Select>
          );
        }

        if (
          field.name === "business_head" ||
          field.name === "function_role_id"
        ) {
          if (field.name === "business_head") {
            return (
              <Select
                {...commonProps}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {dropdownData[field.name]?.map((item) => (
                  <Option key={item._id} value={item._id}>
                    {item.first_name} {item.last_name}
                  </Option>
                ))}
              </Select>
            );
          } else {
            return (
              <Select
                {...commonProps}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {dropdownData[field.name]?.map((item) => (
                  <Option key={item._id} value={item._id}>
                    {item.functionName}
                  </Option>
                ))}
              </Select>
            );
          }
        }

        if (field.options && field.options.length > 0) {
          return (
            <Select {...commonProps}>
              {field.options.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          );
        }

        return (
          <Select {...commonProps} placeholder="Loading options...">
            <Option value="" disabled>
              No options available
            </Option>
          </Select>
        );

      case "multiselect":
        return (
          <Select
            {...commonProps}
            mode="multiple"
            showSearch
            showArrow
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {field.options?.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        );

      case "radio":
        return (
          <Radio.Group {...commonProps}>
            {field.options?.map((option) => (
              <Radio key={option.value} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        );

      case "checkbox":
        return (
          <Checkbox.Group {...commonProps}>
            {field.options?.map((option) => (
              <Checkbox key={option.value} value={option.value}>
                {option.label}
              </Checkbox>
            ))}
          </Checkbox.Group>
        );

      case "date":
        return (
          <DatePicker
            {...commonProps}
            format="DD-MM-YYYY"
            disabledDate={(current) =>
              field.name === "deadline_other_docs" &&
              current &&
              current < moment().startOf("day")
            }
          />
        );

      case "datetime":
        return (
          <DatePicker {...commonProps} showTime format="DD-MM-YYYY HH:mm:ss" />
        );

      case "time":
        return <DatePicker {...commonProps} picker="time" format="HH:mm" />;

      // DynamicFormRenderer.js - inside renderField(field)

      case "file":
        // If it's a specific 'cv_file' using native input (your current implementation)
        if (field.name === "cv_file") {
          // ... your existing native input logic ...
          return (
            <input
              className="ant-input"
              type="file"
              accept=".pdf, .doc, .docx"
              disabled={disabled || field.disabled}
              multiple={field.multiple || false}
              // NOTE: The value will be set via Form.Item's getValueFromEvent
              // You still need to handle message/validation here for immediate feedback
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                  const file = files[0];
                  const fileName = file.name.toLowerCase();
                  const isValidFile = [".pdf", ".doc", ".docx"].some((ext) =>
                    fileName.endsWith(ext)
                  );
                  if (!isValidFile) {
                    message.error(
                      "Only .doc, .docx and pdf CV format is allowed."
                    );
                    e.target.value = ""; // Clear input if invalid
                  }
                  // DO NOT set fields value here, the Form.Item will handle it via getValueFromEvent
                }
              }}
            />
          );
        }

        // For all other file fields, use Dragger
        return (
          <Dragger
            {...commonProps}
            // NO need for beforeUpload or onRemove to manually call form.setFieldsValue
            // The Form.Item handles the value, and the fileList state is just for UI.

            // This is just for UI. The Form.Item value is set by getValueFromEvent.
            beforeUpload={() => false}
            fileList={fileList[field.name] || []}
            onChange={(info) => {
              let newFileList = [...info.fileList];

              // 1. Limit to maxCount
              if (!field.multiple) {
                newFileList = newFileList.slice(-1);
              }

              // 2. Set UI list (Optional, but good practice)
              setFileList((prev) => ({
                ...prev,
                [field.name]: newFileList,
              }));

              // 3. You can still use the onChange for immediate validation/messages
              const { status, file } = info;
              if (status === "done") {
                // This is for files uploaded to a remote server, which you prevent with return false in beforeUpload.
              } else if (status === "error") {
                message.error(`${file.name} file upload failed.`);
              }
            }}
            // Note: maxCount and multiple props are fine.
            maxCount={field.multiple ? undefined : 1}
            multiple={field.multiple || false}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              {field.multiple
                ? "Click or drag files to this area to upload"
                : "Click or drag file to this area to upload"}
            </p>
            {/* ... Dragger content ... */}
          </Dragger>
        );

      case "autocomplete":
        return (
          <Select
            {...commonProps}
            showSearch
            allowClear
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {field.options?.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        );

      default:
        return <Input {...commonProps} />;
    }
  };

  const handleSubmit = async (values) => {
    try {
      console.log("Form values before processing:", values);

      const defaultFields = {};
      const dynamicFieldsData = [];
      const allFields = formConfig.fields || [];

      allFields.forEach((field) => {
        const fieldValue = values[field.name];

        if (defaultFieldNames.includes(field.name)) {
          if (fieldValue !== undefined && fieldValue !== null) {
            defaultFields[field.name] = fieldValue;
          }
        } else {
          const shouldInclude =
            fieldValue !== undefined &&
            fieldValue !== null &&
            fieldValue !== "" &&
            !(Array.isArray(fieldValue) && fieldValue.length === 0);

          if (
            shouldInclude ||
            field.fieldType === "checkbox" ||
            field.fieldType === "file"
          ) {
            const dynamicField = {
              fieldName: field.name,
              fieldLabel: field.label || field.fieldLabel,
              fieldType: field.fieldType,
              required: field.required || false,
            };

            if (field.fieldType === "file") {
              if (fieldValue instanceof File) {
                console.log(
                  `File field ${field.name} has file:`,
                  fieldValue.name
                );
                dynamicField.value = fieldValue;
              } else {
                console.log(`File field ${field.name} is empty`);
                dynamicField.value = "";
              }
            } else if (
              field.fieldType === "date" ||
              field.fieldType === "datetime"
            ) {
              dynamicField.value = fieldValue
                ? moment(fieldValue).toISOString()
                : null;
            } else if (field.fieldType === "time") {
              dynamicField.value = fieldValue
                ? moment(fieldValue).format("HH:mm")
                : null;
            } else if (field.fieldType === "multiselect") {
              dynamicField.value = Array.isArray(fieldValue)
                ? fieldValue
                : fieldValue
                ? [fieldValue]
                : [];
            } else if (field.fieldType === "checkbox") {
              dynamicField.value = Boolean(fieldValue);
            } else {
              dynamicField.value = fieldValue;
            }

            if (field.options && field.options.length > 0) {
              dynamicField.options = field.options;
            }

            if (field.placeholder) {
              dynamicField.placeholder = field.placeholder;
            }

            if (field.helpText) {
              dynamicField.helpText = field.helpText;
            }

            if (field.disabled !== undefined) {
              dynamicField.disabled = field.disabled;
            }

            dynamicFieldsData.push(dynamicField);
          }
        }
      });

      const payload = {
        ...defaultFields,
        formName: formConfig.formName || "Onboarding Form",
        ...(formConfig.formDescription && {
          formDescription: formConfig.formDescription,
        }),
        dynamicFields: dynamicFieldsData,
      };

      console.log("Final payload:", payload);
      console.log(
        "Dynamic fields with files:",
        dynamicFieldsData.filter((f) => f.fieldType === "file")
      );

      if (onSubmit) {
        await onSubmit(payload);
      }
    } catch (error) {
      console.error("Form submission error:", error);
      message.error("Failed to submit form");
      throw error;
    }
  };

  const handleReset = () => {
    form.resetFields();
    setFileList({});
  };

  const getColumnSpan = (field) => {
    if (field.fieldType === "textarea" || field.fieldType === "file") {
      return { xs: 24 };
    }

    if (
      field.fieldType === "radio" &&
      field.options &&
      field.options.length > 2
    ) {
      return { xs: 24 };
    }

    // Full width on small screens, half width from tablet up - a fixed
    // span={12} would keep two cramped columns even on a phone.
    return { xs: 24, sm: 24, md: 12 };
  };
  const getFileValueFromEvent = (field) => (e) => {
    if (Array.isArray(e)) {
      return e;
    }

    // For native <input type="file"> (used for cv_file)
    if (e.target && e.target.files) {
      // Return the raw File object or array of Files
      return field.multiple ? Array.from(e.target.files) : e.target.files[0];
    }

    // For Ant Design Upload/Dragger
    if (e.fileList) {
      // Check if there are files and the last one exists
      const latestFile = e.fileList[e.fileList.length - 1];

      // If the file is removed, return null or undefined to clear the field value
      if (e.file.status === "removed" || e.fileList.length === 0) {
        return null;
      }

      // Return the raw File object from the UploadFile structure
      return field.multiple
        ? e.fileList.map((f) => f.originFileObj).filter(Boolean)
        : latestFile.originFileObj;
    }

    return e;
  };

  if (!formConfig || (!formConfig.fields && !formConfig.sections)) {
    return <div>No form configuration found</div>;
  }

  const sections = formConfig.sections || [
    {
      sectionName: formConfig.formName || "Form",
      sectionKey: "default",
      fields: formConfig.fields || [],
    },
  ];

  return (
    <Card>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={initialValues}
      >
        {sections.map((section, sectionIdx) => (
          <div
            key={section.sectionKey || sectionIdx}
            style={{ marginBottom: 32 }}
          >
            <h3 style={{ marginBottom: 8 }}>{section.sectionName}</h3>
            <Divider style={{ marginTop: 0, marginBottom: 24 }} />
            <Row gutter={[16, 24]}>
              {section.fields
                .sort((a, b) => a.order - b.order)
                .flatMap((field, index) => {
                  const col = (
                    <Col key={field.fieldId || index} {...getColumnSpan(field)}>
                      <Form.Item
                        label={field.label}
                        name={field.name}
                        rules={getValidationRules(field)}
                        help={field.helpText}
                        valuePropName={
                          field.fieldType === "checkbox" ? "checked" : "value"
                        }
                        getValueFromEvent={
                          field.fieldType === "file"
                            ? getFileValueFromEvent(field)
                            : undefined
                        }
                      >
                        {renderField(field)}
                      </Form.Item>
                    </Col>
                  );
                  const isAfterPresentLast =
                    (field.name || field.fieldName) === PRESENT_ADDRESS_LAST_FIELD;
                  if (isAfterPresentLast) {
                    return [
                      col,
                      <Col key="same-as-present-address" span={24}>
                        <Form.Item name="same_as_present_address" valuePropName="checked" noStyle>
                          <Checkbox
                            onChange={(e) => {
                              if (e.target.checked) {
                                const values = {};
                                PERMANENT_TO_PRESENT_MAP.forEach(({ from, to }) => {
                                  values[to] = form.getFieldValue(from);
                                });
                                form.setFieldsValue(values);
                              }
                            }}
                          >
                            {SAME_AS_PRESENT_ADDRESS_LABEL}
                          </Checkbox>
                        </Form.Item>
                      </Col>,
                    ];
                  }
                  return [col];
                })}
            </Row>
          </div>
        ))}

        {(showSubmitButton || showResetButton) && (
          <Row justify="center" style={{ marginTop: 24 }}>
            <Col>
              <div style={{ textAlign: "center" }}>
                {showSubmitButton && (
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    style={{ marginRight: showResetButton ? 16 : 0 }}
                  >
                    {submitButtonText}
                  </Button>
                )}
                {showResetButton && (
                  <Button onClick={handleReset}>{resetButtonText}</Button>
                )}
              </div>
            </Col>
          </Row>
        )}
      </Form>
    </Card>
  );
};

export default DynamicFormRenderer;
